import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { ReportRerunSummary, ReportSummaryItemApi } from "../api/Reports";

/**
 * UUID of the immediate parent report for a rerun, from workflow payload or reruns API.
 */
export function getRerunParentReportUuid(
  report: ReportSummaryItemApi | undefined,
  reportReruns: ReportRerunSummary[] | undefined
): string | null {
  if (!report) return null;
  const fromPayload = report.step_payload?.rerun_from_parent_uuid;
  if (typeof fromPayload === "string" && fromPayload.length > 0) {
    return fromPayload;
  }
  if (!reportReruns?.length) return null;
  const entry = reportReruns.find((r) => r.report_uuid === report.report_uuid);
  if (
    entry?.parent_report_uuid &&
    typeof entry.parent_report_uuid === "string" &&
    entry.parent_report_uuid.length > 0
  ) {
    return entry.parent_report_uuid;
  }
  return null;
}

export function isRerunReport(
  report: ReportSummaryItemApi | undefined,
  reportReruns: ReportRerunSummary[] | undefined
): boolean {
  return getRerunParentReportUuid(report, reportReruns) !== null;
}

/**
 * Other completed reports in the same rerun family (excludes the current report).
 */
export function getOtherRerunReports(
  currentReportUuid: string,
  reportReruns: ReportRerunSummary[] | undefined
): ReportRerunSummary[] {
  if (!reportReruns?.length) return [];
  return reportReruns
    .filter(
      (r) =>
        r.report_uuid !== currentReportUuid &&
        typeof r.finished_at === "string" &&
        r.finished_at.length > 0
    )
    .sort(
      (a, b) =>
        new Date(b.finished_at).getTime() - new Date(a.finished_at).getTime()
    );
}

/**
 * Returns rerun rows only when they belong to the report currently in view.
 * Prevents pairing fresh report data with a prior mutation result while loading.
 */
export function getAlignedReportReruns(
  reportUuid: string | undefined,
  fetchedForReportUuid: string | undefined,
  isLoading: boolean,
  reportReruns: ReportRerunSummary[] | undefined
): ReportRerunSummary[] | undefined {
  if (!reportUuid || reportUuid !== fetchedForReportUuid || isLoading) {
    return undefined;
  }
  return reportReruns;
}

export function formatRerunReportLinkDate(
  finishedAt: string,
  options?: { includeYear?: boolean }
): string {
  const pattern = options?.includeYear === false ? "M/d" : "M/d/yyyy";
  try {
    return formatInTimeZone(parseISO(finishedAt), "UTC", pattern);
  } catch {
    return "NA";
  }
}
